// stores/realtimeStore.js
//
// App-level source of truth for the Reverb/Echo websocket connection state.
//
// Populated once at bootstrap by src/realtime/echo.js, which binds the raw
// pusher-js connection 'state_change'. Kept as a Pinia store — NOT a
// component-scoped composable — because the connection is a singleton that
// outlives every page in this heavily-navigated Framework7 SPA.
//
// NOT persisted: connection state is inherently live. A persisted "connected"
// would be a lie on the next cold boot, before the socket has actually opened.
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// The five UI-facing states. Normalized from pusher-js's six raw connection
// states (initialized / connecting / connected / unavailable / failed /
// disconnected) by src/realtime/echo.js → mapConnectionState().
//
// We keep the transient 'reconnecting' (pusher's auto-retrying 'unavailable')
// distinct from the terminal 'failed'. @laravel/echo-vue's own
// useConnectionStatus() collapses both into 'failed', which a "reconnecting…"
// UI indicator must be able to tell apart — hence our own mapping + store.
export const REALTIME_STATUS = Object.freeze({
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
  FAILED: 'failed',
});

export const useRealtimeStore = defineStore('realtime', () => {
  // One of REALTIME_STATUS.*. Starts 'disconnected' until initRealtime() runs.
  const status = ref(REALTIME_STATUS.DISCONNECTED);

  // Epoch ms of the last successful 'connected' transition (null until first).
  // Feature screens can use this to decide how much to refetch after a gap —
  // the websocket itself has no replay buffer.
  const lastConnectedAt = ref(null);

  const isConnected = computed(() => status.value === REALTIME_STATUS.CONNECTED);

  // True while the socket is establishing OR transparently retrying after a
  // drop — the "show a subtle reconnecting hint, lean on the safety poll" case.
  const isConnecting = computed(
    () =>
      status.value === REALTIME_STATUS.CONNECTING ||
      status.value === REALTIME_STATUS.RECONNECTING,
  );

  function setStatus(next) {
    status.value = next;
  }

  function markConnected() {
    status.value = REALTIME_STATUS.CONNECTED;
    lastConnectedAt.value = Date.now();
  }

  return {
    status,
    lastConnectedAt,
    isConnected,
    isConnecting,
    setStatus,
    markConnected,
  };
});
